import type { Besoin } from '@/lib/besoins';

interface Props {
  besoin: Besoin;
  isMine: boolean;
  isWinner: boolean;
  propositionsCount: number;
  myProposition: boolean;
  onOpenDetail: () => void;
  onProposer: () => void;
}

function fmtAmount(value: number | null, devise: string): string {
  const n = Number(value) || 0;
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise}`;
}

function fmtDate(value: string): string {
  try {
    return new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

/** Carte d'un besoin : résumé, budget et action principale selon le rôle. */
export default function BesoinCard({
  besoin,
  isMine,
  isWinner,
  propositionsCount,
  myProposition,
  onOpenDetail,
  onProposer,
}: Props) {
  const devise = besoin.monaie || 'MAD';
  const budget = Number(besoin.budget) || 0;
  const attribue = besoin.statut === 'attribue';
  const clos = besoin.statut === 'clos';

  return (
    <article className="flex flex-col bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 h-full">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className="text-sm md:text-base font-bold font-heading text-foreground-950 line-clamp-2 leading-snug">{besoin.titre}</h3>
          <p className="text-[11px] text-foreground-500 mt-1 truncate">
            {besoin.auteur_nom || 'Membre Zifek'} · {fmtDate(besoin.created_at)}
          </p>
        </div>
        <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${attribue ? 'bg-primary-100 text-primary-700' : clos ? 'bg-background-200/70 text-foreground-600' : 'bg-accent-100 text-accent-900'}`}>
          {attribue ? 'Attribué' : clos ? 'Clos' : 'Ouvert'}
        </span>
      </div>

      {besoin.description && (
        <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">{besoin.description}</p>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {besoin.categorie && (
          <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-medium whitespace-nowrap">
            <i className="ri-briefcase-4-line mr-1"></i>
            {besoin.categorie}
          </span>
        )}
        {besoin.ville && (
          <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
            <i className="ri-map-pin-line mr-1"></i>
            {besoin.ville}
          </span>
        )}
        {besoin.annonce_le && (
          <span className="px-2 py-0.5 rounded-full bg-accent-50 text-accent-800 text-[10px] font-medium whitespace-nowrap">
            <i className="ri-megaphone-line mr-1"></i>
            Annoncé
          </span>
        )}
      </div>

      <div className="mt-auto flex items-end justify-between gap-2 pt-3 border-t border-background-200/60">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">
            {budget > 0 ? 'Prix proposé' : 'Prix'}
          </p>
          <p className="text-sm font-bold text-foreground-950 whitespace-nowrap">
            {budget > 0 ? fmtAmount(budget, devise) : 'À négocier'}
          </p>
        </div>

        {isMine ? (
          <button
            onClick={onOpenDetail}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
          >
            <i className="ri-file-list-3-line"></i>
            {propositionsCount} proposition{propositionsCount > 1 ? 's' : ''}
          </button>
        ) : isWinner ? (
          <button
            onClick={onOpenDetail}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            <i className="ri-chat-3-line"></i>
            Détails & contact
          </button>
        ) : (
          <button
            onClick={onProposer}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            <i className={myProposition ? 'ri-edit-line' : 'ri-hand-heart-line'}></i>
            {myProposition ? 'Ma proposition' : 'Je suis disponible'}
          </button>
        )}
      </div>
    </article>
  );
}